import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathHomePage, applications, protocols } from "../../../../../../config/constants";
import { renderToolTip } from "../../../../../../config/functions";
import { getProtocolTitle } from "../../../../../../config/validateProtocols";
// Components
import {
  MainDiv,
  Header,
  Title,
  OpenButton,
  OpenButtonImg,
  PlotDiv,
  AxisY,
  HeatMapContainer,
  HeatMapWithSessionsContainer,
  HeatMapStyled,
  AxisX,
  SessionsContainer,
  SessionsAligner,
  ReportsLink,
  ReportsImg,
  SessionsNumber,
} from "./styles";
// Assets
import ArrowUpImg from "../../../../../../assets/icons/ico_arrow_up_blue.svg";
import ArrowDownImg from "../../../../../../assets/icons/ico_arrow_down_blue.svg";
import ReportsIco from "../../../../../../assets/icons/ico_reports.svg";

const ActivitiesPerformed = ({ activitiesPerformed }) => {
  const [showPlot, setShowPlot] = useState(false);
  const [activitySessionsPerWeek, setActivitySessionsPerWeek] = useState([]);
  const [xLabels] = useState(Array.from({ length: 12 }, (_, i) => i + 1));
  const [yLabels, setYLabels] = useState([]);
  const [sessionsPerActivity, setSessionsPerActivity] = useState([]);
  const { patientId } = useParams();
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const [t] = useTranslation("global");

  // Set Heat Map data
  useEffect(() => {
    setActivitySessionsPerWeek(
      activitiesPerformed.map((activity) => {
        return [...Object.values(activity).map((weekSessions) => weekSessions)[0]];
      })
    );

    setYLabels(
      activitiesPerformed.map((activity) => {
        const protocolId = Number(Object.keys(activity)[0]);
        return protocolId === protocols.clinicAndHome.PINBALL.id
          ? `${t(getProtocolTitle("protocols", protocolId))} (RGSclinic)`
          : t(getProtocolTitle("protocols", protocolId));
      })
    );

    setSessionsPerActivity(
      activitiesPerformed.map((activity) => {
        return [...Object.values(activity).map((weekSessions) => weekSessions)[0]].reduce(
          (accumulator, sessionsValue) => accumulator + sessionsValue,
          0
        );
      })
    );

    return () => {
      setActivitySessionsPerWeek([]);
      setYLabels([]);
    };
  }, [activitiesPerformed, t]);

  const renderSessionsNumber = () => {
    return sessionsPerActivity.map((sessionsNumber, i) => {
      const protocolId = Number(Object.keys(activitiesPerformed[i]));

      const software = protocolId === protocols.clinicAndHome.PINBALL.id ? applications.clinic.name : applications.plus.name;

      const protocolTitle =
        protocolId === protocols.clinicAndHome.PINBALL.id
          ? `${t(getProtocolTitle("protocols", protocolId))} (RGSclinic)`
          : t(getProtocolTitle("protocols", protocolId));

      return (
        <SessionsAligner key={i}>
          <ReportsLink sessions={sessionsNumber} to={`${pathHomePage}/reports/${clinicianToken}/${software}/${patientId}/${protocolId}`}>
            <ReportsImg src={ReportsIco} alt="reports-btn" />
          </ReportsLink>
          <SessionsNumber data-for="sessionsNumber" data-tip={`Number of sessions performed by activity: ${protocolTitle}`}>
            {!sessionsNumber ? "-" : sessionsNumber}
          </SessionsNumber>
        </SessionsAligner>
      );
    });
  };

  const isThereSomeSession = (pos) => {
    const protocolSessions = [...Object.values(activitiesPerformed[pos]).map((i) => i)[0]];
    const total = protocolSessions.reduce((acc, num) => acc + num, 0);
    return total > 0;
  };

  return (
    <MainDiv>
      <Header>
        <Title>Activities performed</Title>
        <OpenButton onClick={() => setShowPlot(!showPlot)}>
          <OpenButtonImg src={showPlot ? ArrowUpImg : ArrowDownImg} alt="arrow-ico" />
        </OpenButton>
      </Header>
      {showPlot && (
        <PlotDiv>
          <AxisY>Activities</AxisY>
          <HeatMapContainer>
            <HeatMapWithSessionsContainer>
              <HeatMapStyled
                data={activitySessionsPerWeek}
                xLabels={xLabels}
                yLabels={yLabels}
                xLabelsStyle={() => ({
                  margin: "1px",
                  fontSize: "14px",
                })}
                yLabelsStyle={(x) => ({
                  margin: "1px",
                  color: !isThereSomeSession(x) ? "#808080" : "#000",
                  fontSize: "14px",
                  fontWeight: "600",
                  paddingRight: "10px",
                })}
                cellStyle={(x, y, ratio) => ({
                  margin: "1px",
                  width: "40px",
                  background: !isThereSomeSession(x) ? "rgba(128, 128, 128, 0.20)" : ratio > 0 ? "#39B54A" : "#fff",
                  border: ratio > 0 ? "1px solid #39B54A" : "1px solid #e2e2e2",
                  borderRadius: "0",
                })}
                cellHeight="25px"
                xLabelsPos="bottom"
              />
              <SessionsContainer>
                {renderSessionsNumber()}
                {renderToolTip("sessionsNumber")}
              </SessionsContainer>
            </HeatMapWithSessionsContainer>
            <AxisX>Weeks</AxisX>
          </HeatMapContainer>
        </PlotDiv>
      )}
    </MainDiv>
  );
};

export default ActivitiesPerformed;
